import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ClipboardList, Image, RefreshCw, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  if (status === 'approved') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
      <CheckCircle2 className="h-3 w-3" /> Approved
    </span>
  );
  if (status === 'rejected') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
      <XCircle className="h-3 w-3" /> Rejected
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
      <Clock className="h-3 w-3" /> Pending
    </span>
  );
};

const DieselDeletionLogPage = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [proofImageUrl, setProofImageUrl] = useState(null);
  const [viewingProof, setViewingProof] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ module: 'diesel' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await axios.get(`${API}/delete-requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data);
    } catch (e) {
      toast.error('Failed to load deletion log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [statusFilter]);

  const canApprove = user?.is_admin || user?.is_super_admin || user?.permissions?.can_approve_deletions === true;

  const handleApprove = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deletion approved and executed');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/reject`, { note: 'Rejected' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request rejected');
      fetchRequests();
    } catch (e) {
      toast.error('Failed to reject');
    }
  };

  const viewProofImage = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/delete-requests/${reqId}/proof-image`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      setProofImageUrl(url);
      setViewingProof(true);
    } catch (e) {
      toast.error('Failed to load proof image');
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-red-600" />
            Diesel Deletion Audit Log
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Permanent record of all diesel entry deletion requests
          </p>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && canApprove && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-sm font-semibold border border-amber-200">
              <Clock className="h-4 w-4" />
              {pendingCount} Pending Approval
            </span>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9 text-sm">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requests</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchRequests} className="h-9">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Requests', count: requests.length, color: 'text-slate-700', bg: 'bg-slate-100' },
          { label: 'Approved & Deleted', count: requests.filter(r => r.status === 'approved').length, color: 'text-green-700', bg: 'bg-green-100' },
          { label: 'Rejected', count: requests.filter(r => r.status === 'rejected').length, color: 'text-red-700', bg: 'bg-red-100' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg border p-4 flex items-center gap-3">
            <div className={`${s.bg} rounded-lg p-2`}>
              <ClipboardList className={`h-5 w-5 ${s.color}`} />
            </div>
            <div>
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Log Table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
          </div>
        ) : requests.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <ClipboardList className="h-10 w-10 mx-auto mb-2 text-slate-300" />
            No deletion requests found
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date Requested</TableHead>
                <TableHead>Record</TableHead>
                <TableHead>Reason Given</TableHead>
                <TableHead>Requested By</TableHead>
                <TableHead>Proof</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reviewed By</TableHead>
                <TableHead>Review Note</TableHead>
                {canApprove && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map(req => (
                <TableRow key={req.id} className={req.status === 'pending' ? 'bg-amber-50/50' : ''}>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                    {req.created_at ? format(new Date(req.created_at), 'dd MMM yy, HH:mm') : '-'}
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <span className="text-sm font-medium text-slate-800 line-clamp-2">{req.record_display || req.record_id}</span>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <span className="text-sm text-slate-600 italic">
                      {req.reason || <span className="text-slate-400">No reason given</span>}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="font-medium">{req.requested_by_name}</span>
                    <br />
                    <span className="text-xs text-slate-400">{req.requested_by_email}</span>
                  </TableCell>
                  <TableCell>
                    {req.proof_image ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        onClick={() => viewProofImage(req.id)}
                        data-testid={`view-proof-${req.id}`}
                      >
                        <Image className="h-3 w-3" /> View
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={req.status} />
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {req.reviewed_by || '-'}
                    {req.reviewed_at && (
                      <div className="text-xs text-slate-400">
                        {format(new Date(req.reviewed_at), 'dd MMM yy')}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-xs">
                    <span className="line-clamp-2">{req.review_note || '-'}</span>
                  </TableCell>
                  {canApprove && (
                    <TableCell className="text-right">
                      {req.status === 'pending' && (
                        <div className="flex gap-1.5 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-green-700 border-green-300 hover:bg-green-50"
                            onClick={() => handleApprove(req.id)}
                            data-testid={`approve-delete-log-${req.id}`}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50"
                            onClick={() => handleReject(req.id)}
                            data-testid={`reject-delete-log-${req.id}`}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Proof Image Viewer */}
      <Dialog open={viewingProof} onOpenChange={(open) => {
        if (!open) {
          setViewingProof(false);
          if (proofImageUrl) URL.revokeObjectURL(proofImageUrl);
          setProofImageUrl(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Deletion Proof Image</DialogTitle>
          </DialogHeader>
          {proofImageUrl && (
            <img src={proofImageUrl} alt="Proof" className="w-full rounded-lg object-contain max-h-[60vh]" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DieselDeletionLogPage;
